/**
 * SF-5127: the help desk sending domain, for "Reply from: the company's support address". The
 * company adds these DNS records for the domain of its support inbox address, and once every one
 * checks out help desk emails can come from that address.
 *
 *   GET    /admin/v1/companies/:id/help-desk/sending-domain        -> null | SendingDomain
 *   POST   /admin/v1/companies/:id/help-desk/sending-domain        -> SendingDomain (set up)
 *   POST   /admin/v1/companies/:id/help-desk/sending-domain/check  -> SendingDomain (check again)
 *   DELETE /admin/v1/companies/:id/help-desk/sending-domain        -> null (Reply from goes back
 *          to ServeFirst)
 *
 * The data provider unwraps sf-api's `{ success, data }` envelope, except that a null `data`
 * comes back as the whole envelope (`response.data ?? response`), so `parseSendingDomain` reads
 * anything that is not a domain record as "none".
 *
 * Writes go through the page's saver (`send`), so they follow the one-save-at-a-time rule and
 * re-read the company afterwards, after a failed one too (Reply from and what customers see can
 * change). Each write puts the domain sf-api returned into THAT company's cache entry, not the
 * one on screen; a failed one reads that company's domain again. The page also reads the domain
 * again whenever it re-reads the company it shows (Refresh, refocus), so both stay in step.
 */
import { useCustomMutation, useDataProvider, useOnError } from '@refinedev/core';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Company } from '../list';
import { COMPANIES_API_BASE } from './companyDeleteClient';

export interface HelpDeskDnsRecord {
  kind: 'CNAME' | 'TXT';
  host: string;
  value: string;
  /** null = not checked yet. */
  valid: boolean | null;
}

export interface HelpDeskSendingDomain {
  domain: string;
  valid: boolean;
  validated_at: string | null;
  error_reason: string | null;
  /** Only the required records: every one must be found for the domain to be verified. */
  records: HelpDeskDnsRecord[];
  /** A DMARC record to add when the domain has none. Optional advice; never checked. */
  dmarc_suggestion: { host: string; value: string } | null;
}

export type SendingDomainAction = 'setup' | 'check' | 'remove';

const sendingDomainUrl = (companyId: string) =>
  `/${COMPANIES_API_BASE}/${encodeURIComponent(companyId)}/help-desk/sending-domain`;

export const sendingDomainKey = (companyId: string) => [
  'companies',
  companyId,
  'help-desk-sending-domain',
];

/** The domain record, or null for "none" (or anything that is not a record). */
export function parseSendingDomain(raw: unknown): HelpDeskSendingDomain | null {
  const r = raw as Partial<HelpDeskSendingDomain> | null | undefined;
  if (!r || typeof r !== 'object' || typeof r.domain !== 'string') return null;
  return {
    domain: r.domain,
    valid: r.valid === true,
    validated_at: typeof r.validated_at === 'string' ? r.validated_at : null,
    error_reason: typeof r.error_reason === 'string' && r.error_reason ? r.error_reason : null,
    records: Array.isArray(r.records) ? r.records : [],
    dmarc_suggestion: parseDmarcSuggestion(r.dmarc_suggestion),
  };
}

function parseDmarcSuggestion(raw: unknown): HelpDeskSendingDomain['dmarc_suggestion'] {
  const d = raw as { host?: unknown; value?: unknown } | null | undefined;
  return d && typeof d.host === 'string' && typeof d.value === 'string'
    ? { host: d.host, value: d.value }
    : null;
}

/** The company's sending domain (null = none set up). Read only while `enabled`. */
export function useHelpDeskSendingDomain(companyId: string, enabled: boolean) {
  const getDataProvider = useDataProvider();
  // Failures go to the auth provider like every useCustom read (a dead session logs out).
  const { mutate: checkError } = useOnError();
  return useQuery<HelpDeskSendingDomain | null>({
    queryKey: sendingDomainKey(companyId),
    queryFn: async () => {
      const custom = getDataProvider().custom;
      if (!custom) throw new Error('The data provider has no custom request.');
      try {
        const { data } = await custom({ url: sendingDomainUrl(companyId), method: 'get' });
        return parseSendingDomain(data);
      } catch (err) {
        checkError(err);
        throw err;
      }
    },
    enabled: enabled && !!companyId,
    // Never show another company's domain while this one loads.
    keepPreviousData: false,
    retry: false,
    refetchOnWindowFocus: false,
  });
}

export function sendingDomainActionArgs(companyId: string, action: SendingDomainAction) {
  return {
    url: `${sendingDomainUrl(companyId)}${action === 'check' ? '/check' : ''}`,
    method: action === 'remove' ? ('delete' as const) : ('post' as const),
    values: {},
    errorNotification: false as const,
    successNotification: false as const,
  };
}

/**
 * Runs a sending-domain action for a company and resolves with the company fields it changed,
 * for the page's saver (`send`). Removing the domain puts Reply from back to ServeFirst.
 */
export function useSendingDomainAction() {
  const { mutateAsync } = useCustomMutation<Record<string, unknown>>();
  const queryClient = useQueryClient();
  return (action: SendingDomainAction) =>
    async (companyId: string): Promise<Partial<Company>> => {
      let data: unknown;
      try {
        ({ data } = await mutateAsync(sendingDomainActionArgs(companyId, action)));
      } catch (err) {
        // A failed action can still have changed the domain (a check that finds the registration
        // gone saves it as not verified, then answers 502), so read THAT company's domain again,
        // even when it is no longer on screen. A failed read leaves the query in error.
        await queryClient.refetchQueries({ queryKey: sendingDomainKey(companyId), exact: true });
        throw err;
      }
      // A read still in flight must not land over the answer to this write.
      await queryClient.cancelQueries({ queryKey: sendingDomainKey(companyId) });
      queryClient.setQueryData(
        sendingDomainKey(companyId),
        action === 'remove' ? null : parseSendingDomain(data),
      );
      return action === 'remove' ? { help_desk_reply_from: 'servefirst' } : {};
    };
}
